import ErrorBoundary from "@/components/ErrorBoundary";
import MaintenanceWrapper from "@/components/MaintenanceWrapper";
import { PortraitLockOverlay } from "@/components/PortraitLockOverlay";
import { UploadProgressToast } from "@/components/UploadProgressToast";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DanceModeProvider } from "@/contexts/DanceModeContext";
import { useUpload } from "@/contexts/UploadContext";
import { I18nProvider, useSEO } from "@/features/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense } from "react";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import {
    BrowserRouter,
    Navigate,
    Outlet,
    Route,
    Routes,
} from "react-router-dom";
import BackgroundVideo from "./components/BackgroundVideo";
import { ServiceWorkerProvider } from "./components/ServiceWorkerProvider";
import { AdvancedModeProvider } from "./contexts/AdvancedModeContext";
import { BackgroundModeProvider } from "./contexts/BackgroundModeContext";
import { BackgroundMusicProvider } from "./contexts/BackgroundMusicContext";
import { DemoModeProvider } from "./contexts/DemoModeContext";
import { DevModeProvider } from "./contexts/DevModeContext";
import { PendingVideoPreviewProvider } from "./contexts/PendingVideoPreviewContext";
import { PublicVideoPoolCommunityFilterProvider } from "./contexts/PublicVideoPoolCommunityContext";
import { ScrubbingTimelineProvider } from "./contexts/ScrubbingTimelineContext";
import { UploadProvider } from "./contexts/UploadContext";
import { VideoDataSaverProvider } from "./contexts/VideoDataSaverContext";
import { VideoModeProvider } from "./contexts/VideoModeContext";
import { VideoSpeedAdjustmentProvider } from "./contexts/VideoSpeedAdjustmentContext";
import { VideoSwipeProvider } from "./contexts/VideoSwipeContext";
import { AuthProvider } from "./hooks/useAuth";
import { useGlobalDisablePictureInPicture } from "./hooks/useGlobalDisablePictureInPicture";
import { useOrientationLock } from "./hooks/useOrientationLock";
import { useUserLanguageSync } from "./hooks/useUserLanguageSync";
import { AppLifecycleProvider } from "./lifecycle/AppLifecycleManager";

// Lazy load the CookieConsent component to avoid SSR issues
const CookieConsent = lazyWithRetry(() => import("@/components/CookieConsent"));

// Lazy load heavy pages
const Index = lazyWithRetry(() => import("./pages/Index"));
const Booking = lazyWithRetry(() => import("./pages/Booking"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AdminPage = lazyWithRetry(() => import("./pages/admin"));
const RegistrationSuccess = lazyWithRetry(() => import("./pages/RegistrationSuccess"));
const SessionTeilnehmerRegistrierung = lazyWithRetry(
  () => import("./pages/SessionTeilnehmerRegistrierung")
);
const SessionInvite = lazyWithRetry(() => import("./pages/SessionInvite"));
const Terminal = lazyWithRetry(() => import("./pages/Terminal"));
const AGB = lazyWithRetry(() => import("./pages/AGB"));
const Datenschutz = lazyWithRetry(() => import("./pages/Datenschutz"));
const Impressum = lazyWithRetry(() => import("./pages/Impressum"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const GoogleCallback = lazyWithRetry(() => import("./pages/auth/GoogleCallback"));
const AuthCallback = lazyWithRetry(() => import("./pages/auth/callback"));
const EventHandoutPage = lazyWithRetry(() => import("./pages/EventHandoutPage"));
const EventTicketRedeemPage = lazyWithRetry(
  () => import("./pages/EventTicketRedeemPage")
);
const CommunityQr = lazyWithRetry(() => import("./pages/CommunityQr"));

import { CoupleUpLoginSpinner } from "@/components/spinners/DanceSpinners";

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <CoupleUpLoginSpinner size="large" />
  </div>
);

// Kein Spinner für öffentliche Seiten
const NoSpinner = () => null;

// Erstellen Sie den QueryClient außerhalb der Komponente
// Mit Query-Deduplizierung und Caching für bessere Performance
// STATE-OF-THE-ART: Professionelle Konfiguration für PWA/Capacitor Apps
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 Minuten - Daten sind 5 Min "frisch"
      gcTime: 10 * 60 * 1000, // 10 Minuten (früher cacheTime) - Cache bleibt 10 Min
      // ⚠️ RESUME-BUG FIX (2026-02-10): refetchOnWindowFocus MUSS false sein!
      // Der AppLifecycleManager ist die EINZIGE Stelle, die Resume-Refetches steuert.
      // Wenn TanStack Query ZUSÄTZLICH bei Focus refetcht, entstehen 4-5 parallele
      // Netzwerk-Zyklen (TQ-Refetch + invalidateQueries + refetchQueries + SIGNED_IN
      // durch Token-Refresh + fetchSubscription), die sich gegenseitig blockieren.
      // Ergebnis: refetchQueries() hängt endlos → Dashboard-Daten bleiben leer.
      // Siehe: docs/session-notes/2026-02-10-resume-bug-root-cause-fix.md
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // Wird vom AppLifecycleManager via "online" Event gesteuert
      retry: (failureCount, error) => {
        // Retry nur bei Netzwerkfehlern/5xx, max. 3 Versuche
        const errorMessage = error?.message || "";
        const isNetworkError =
          errorMessage.includes("Failed to fetch") ||
          errorMessage.includes("NetworkError") ||
          errorMessage.includes("Network request failed");
        const isServerError =
          errorMessage.includes("500") ||
          errorMessage.includes("502") ||
          errorMessage.includes("503") ||
          errorMessage.includes("429");

        if ((isNetworkError || isServerError) && failureCount < 3) {
          return true;
        }
        return false;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff: 1s, 2s, 4s, max 5s
      // Placeholder Data: Behalte letzte Daten während Refetch (wichtig für Tab-Wechsel)
      placeholderData: previousData => previousData,
    },
    mutations: {
      // Global: Retry bei Netzwerkfehlern
      retry: (failureCount, error) => {
        const errorMessage = error?.message || "";
        const isNetworkError =
          errorMessage.includes("Failed to fetch") ||
          errorMessage.includes("NetworkError") ||
          errorMessage.includes("Network request failed");
        const isServerError =
          errorMessage.includes("500") ||
          errorMessage.includes("502") ||
          errorMessage.includes("503") ||
          errorMessage.includes("429");

        // Retry nur bei Netzwerkfehlern/5xx, max. 2 Versuche (Mutations sollten nicht zu aggressiv retryen)
        if ((isNetworkError || isServerError) && failureCount < 2) {
          return true;
        }
        return false;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
  },
});

const InternalAppShell: React.FC = () => {
  const { uploadState, clearUpload } = useUpload();

  // Gespeicherte Nutzer-Sprache beim Login anwenden (Hintergrund, keine UI)
  useUserLanguageSync();

  // Portrait-Modus erzwingen (inkl. /terminal – Kiosk nutzt Hochformat)
  const { shouldBlock } = useOrientationLock({ enabled: true });

  return (
    <>
      {/* Portrait-Lock Overlay - zeigt sich wenn Landscape erkannt wird */}
      {shouldBlock && <PortraitLockOverlay />}

      <AdvancedModeProvider>
        <DevModeProvider>
          <DemoModeProvider>
            <DanceModeProvider>
              <BackgroundModeProvider>
                <VideoSpeedAdjustmentProvider>
                  <VideoSwipeProvider>
                    <ScrubbingTimelineProvider>
                      <div className="app-shell relative z-10">
                        <Outlet />
                      </div>
                    </ScrubbingTimelineProvider>
                  </VideoSwipeProvider>
                </VideoSpeedAdjustmentProvider>
              </BackgroundModeProvider>
            </DanceModeProvider>
          </DemoModeProvider>
        </DevModeProvider>
      </AdvancedModeProvider>
      {/* Globale Upload-Progress-Anzeige - nur bei trimming/uploading, nicht bei processing */}
      {uploadState &&
        uploadState.phase !== "processing" &&
        uploadState.phase !== "done" && (
          <UploadProgressToast
            isVisible={!!uploadState}
            progress={uploadState.progress}
            fileName={uploadState.fileName}
            phase={uploadState.phase}
            error={uploadState.error}
            onDismiss={clearUpload}
          />
        )}
    </>
  );
};

// Definieren Sie App als Funktionskomponente mit expliziter React-Typannotation
// HINWEIS: StrictMode ist bereits in main.tsx aktiviert, daher hier nicht erneut verwenden
const PublicAppShell: React.FC = () => {
  // SEO-Optimierungen (HTML lang-Attribut)
  useSEO();

  return (
    <DevModeProvider>
      <DanceModeProvider>
        <div className="app-shell relative z-10">
          {/* Hintergrund-Video nur für Public-Landing/Marketing-Routen */}
          <BackgroundVideo />
          <Outlet />
        </div>
      </DanceModeProvider>
    </DevModeProvider>
  );
};

const InternalProviders: React.FC = () => {
  return (
    <AppLifecycleProvider>
      <ServiceWorkerProvider>
        <UploadProvider>
          <PendingVideoPreviewProvider>
            <InternalAppShell />
          </PendingVideoPreviewProvider>
        </UploadProvider>
      </ServiceWorkerProvider>
    </AppLifecycleProvider>
  );
};

const App: React.FC = () => {
  // Kein Picture-in-Picture beim Verlassen der App (Android/Chrome); zentral für alle Videos.
  useGlobalDisablePictureInPicture();

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <TooltipProvider>
          <Toaster />
          <SonnerToaster />
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
          <ErrorBoundary>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AuthProvider>
                <MaintenanceWrapper>
                  <Suspense fallback={<NoSpinner />}>
                    <Routes>
                    <Route element={<PublicAppShell />}>
                      {/* Öffentliche Seiten mit schlanker Provider-Kette */}
                      <Route path="/" element={<Index />} />
                      <Route path="/booking" element={<Booking />} />
                      <Route
                        path="/imprint"
                        element={
                          <Suspense fallback={<NoSpinner />}>
                            <Impressum />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/impressum"
                        element={
                          <Suspense fallback={<NoSpinner />}>
                            <Impressum />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/datenschutz"
                        element={
                          <Suspense fallback={<NoSpinner />}>
                            <Datenschutz />
                          </Suspense>
                        }
                      />
                      <Route path="/session-invite" element={<SessionInvite />} />
                      <Route path="/community-qr" element={<CommunityQr />} />
                      <Route
                        path="/agb"
                        element={
                          <Suspense fallback={<NoSpinner />}>
                            <AGB />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/payment-success"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <PaymentSuccess />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/registration-success"
                        element={<RegistrationSuccess />}
                      />
                      <Route
                        path="/session-teilnehmer"
                        element={<SessionTeilnehmerRegistrierung />}
                      />
                    </Route>

                    <Route element={<InternalProviders />}>
                      {/* Interne Bereiche mit voller Provider-Kette */}
                      <Route
                        path="/login"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <Login />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/auth/callback"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <AuthCallback />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/auth/google-callback"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <GoogleCallback />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/app-preview"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <VideoModeProvider>
                              <VideoDataSaverProvider>
                                <PublicVideoPoolCommunityFilterProvider>
                                  <BackgroundMusicProvider>
                                    <Dashboard />
                                  </BackgroundMusicProvider>
                                </PublicVideoPoolCommunityFilterProvider>
                              </VideoDataSaverProvider>
                            </VideoModeProvider>
                          </Suspense>
                        }
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <VideoModeProvider>
                              <VideoDataSaverProvider>
                                <PublicVideoPoolCommunityFilterProvider>
                                  <BackgroundMusicProvider>
                                    <Dashboard />
                                  </BackgroundMusicProvider>
                                </PublicVideoPoolCommunityFilterProvider>
                              </VideoDataSaverProvider>
                            </VideoModeProvider>
                          </Suspense>
                        }
                      />
                      <Route
                        path="/event-handouts/berlin-rhythm-trinity-2026.html"
                        element={
                          <Navigate
                            to="/event-handout/berlin-rhythm-trinity-2026"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/event-handouts/prag-share-the-vibe-2026.html"
                        element={
                          <Navigate
                            to="/event-handout/prag-share-the-vibe-2026"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/event-handout/:slug"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <EventHandoutPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/redeem-event-ticket"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <EventTicketRedeemPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <BackgroundMusicProvider>
                              <AdminPage />
                            </BackgroundMusicProvider>
                          </Suspense>
                        }
                      />
                      <Route
                        path="/terminal"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <VideoModeProvider>
                              <BackgroundMusicProvider>
                                <Terminal />
                              </BackgroundMusicProvider>
                            </VideoModeProvider>
                          </Suspense>
                        }
                      />
                    </Route>

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route
                      path="*"
                      element={
                        <Suspense fallback={<NoSpinner />}>
                          <NotFound />
                        </Suspense>
                      }
                    />
                    </Routes>
                  </Suspense>
                </MaintenanceWrapper>
              </AuthProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
};

export default App;
